import React, { useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { userApi } from '../config';
import {
  Clock,
  MapPin,
  Video,
  UserCheck,
  Share2,
  Check,
  X,
  Maximize2,
  ArrowLeft,
} from 'lucide-react';

const BookingStep1: React.FC = () => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();

  const [serviceType, setServiceType] = useState<'class' | 'consult'>('class');
  const [mode, setMode] = useState<'online' | 'offline'>('online');

  // Selected plan index
  const [selectedPlanIndex, setSelectedPlanIndex] = useState(0);

  const location = useLocation();
  const classId = new URLSearchParams(location.search).get('classId');

  const [classDetails, setClassDetails] = useState<any>(null);
  const [instructor, setInstructor] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // UI state matching luxury retreat page
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);
  const [isImageLightboxOpen, setIsImageLightboxOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  React.useEffect(() => {
    setLoadError(null);
    if (classId) {
      const fetchDetails = async () => {
        try {
          const res: any = await userApi.get(`/fetch-class-details?classId=${classId}`);
          if (res.data && res.data.data) {
            const classData = res.data.data.class;
            setClassDetails(classData);
            setInstructor(res.data.data.instructor);

            const fetchedMode = classData?.mode?.toLowerCase();
            if (fetchedMode === 'online') setMode('online');
            else if (fetchedMode === 'offline') setMode('offline');
            else setMode('online');

            setServiceType('class');
          } else {
            setLoadError('Booking details are not available for this session.');
          }
        } catch (err) {
          console.error(err);
          setLoadError('Booking details are not available for this session.');
        }
        setLoading(false);
      };
      fetchDetails();
    } else {
      setLoadError('No booking session selected.');
      setLoading(false);
    }
  }, [classId]);

  const formatINR = (value: unknown) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric <= 0) return null;
    return `₹${numeric.toLocaleString('en-IN')}`;
  };

  const activeDurations = React.useMemo(() => {
    if (!classDetails) return [];

    const pricingType = classDetails?.pricingType?.toLowerCase();

    if (pricingType === 'free') {
      return [{ len: 'FREE SESSION', amt: '₹0' }];
    }

    if (pricingType === 'demo') {
      const demoPlans = [{ len: 'DEMO SESSION', amt: '₹0' }];
      const sessionPrice = formatINR(classDetails.classPrice?.session);
      const monthlyPrice = formatINR(classDetails.classPrice?.monthly);
      const yearlyPrice = formatINR(classDetails.classPrice?.yearly);

      const apiPlans = [
        sessionPrice && { len: '1 SESSION', amt: sessionPrice },
        monthlyPrice && { len: 'MONTHLY', amt: monthlyPrice },
        yearlyPrice && { len: 'YEARLY', amt: yearlyPrice }
      ].filter(Boolean) as Array<{ len: string; amt: string }>;

      if (apiPlans.length > 0) {
        return [...demoPlans, ...apiPlans];
      }

      return demoPlans;
    }

    if (classDetails?.classPrice) {
      const sessionPrice = formatINR(classDetails.classPrice?.session);
      const monthlyPrice = formatINR(classDetails.classPrice?.monthly);
      const yearlyPrice = formatINR(classDetails.classPrice?.yearly);

      return [
        sessionPrice && { len: '1 SESSION', amt: sessionPrice },
        monthlyPrice && { len: 'MONTHLY', amt: monthlyPrice },
        yearlyPrice && { len: 'YEARLY', amt: yearlyPrice }
      ].filter(Boolean) as Array<{ len: string; amt: string }>;
    }

    return [];
  }, [classDetails]);

  React.useEffect(() => {
    if (!classDetails) return;

    const pricingType = classDetails.pricingType?.toLowerCase();
    if (pricingType === 'demo' || pricingType === 'free') {
      setSelectedPlanIndex(0);
      return;
    }

    setSelectedPlanIndex(prev => Math.min(prev, Math.max(activeDurations.length - 1, 0)));
  }, [classDetails, activeDurations.length]);

  const currentPlan = activeDurations[selectedPlanIndex] || activeDurations[0] || { len: '1 SESSION', amt: '₹499' };
  const isRecurringClass = classDetails?.classType === 'recurring';
  const isLongTermPlan = ['monthly', 'yearly'].includes(String(currentPlan?.len || '').toLowerCase());
  const isDateBasedPlan = isRecurringClass || isLongTermPlan;

  const handleShare = async () => {
    const pageUrl = window.location.href;
    const shareTitle = `${classDetails?.classTitle || 'Wellness Session'} | MetaFit`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: `Check out ${classDetails?.classTitle} by ${instructor?.name || 'MetaFit Mentor'} on MetaFit:`,
          url: pageUrl,
        });
        return;
      } catch (err) {
        // dismissed share
      }
    }
    try {
      await navigator.clipboard.writeText(pageUrl);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch {
      // clipboard fallback
    }
  };

  const heroImage =
    classDetails?.classThumbnail ||
    instructor?.image ||
    'https://images.unsplash.com/photo-1545205597-3d9d02c29597?auto=format&fit=crop&w=1200&q=80';

  if (loading || (!loadError && (!classDetails || !instructor))) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-slate-600 text-sm font-medium">Loading session details...</p>
        </div>
      </div>
    );
  }

  if (loadError || !classDetails || !instructor) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl border border-slate-200 p-6 text-center shadow-xs">
          <h2 className="text-lg font-bold text-slate-900 mb-2">Booking Not Available</h2>
          <p className="text-slate-500 text-xs mb-5">
            {loadError || 'This session is missing booking details right now.'}
          </p>
          <button
            onClick={() => navigate(-1)}
            className="px-5 py-2.5 bg-black hover:bg-neutral-800 text-white rounded-full text-xs font-semibold transition-all"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 antialiased pb-28 pt-3">
      {/* Main Container - Matches BookMyAdventure / Retreat UI */}
      <div className="max-w-4xl mx-auto px-3 sm:px-5 space-y-2.5">
        {/* 1. HERO POSTER / BANNER CARD */}
        <div className="w-full rounded-2xl bg-white border border-slate-200 overflow-hidden shadow-2xs">
          <div
            onClick={() => setIsImageLightboxOpen(true)}
            className="relative w-full rounded-2xl overflow-hidden group cursor-pointer aspect-[16/9] sm:aspect-[21/9] bg-slate-100"
          >
            <img
              src={heroImage}
              alt={classDetails?.classTitle || 'Session Flyer'}
              className="w-full h-full object-cover relative z-0 transition-transform duration-500 group-hover:scale-[1.01]"
            />

            {/* Top Right Enlarge */}
            <div className="absolute top-2.5 right-2.5 opacity-90 group-hover:opacity-100 transition-opacity z-10">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-black/60 px-2.5 py-1 text-[10px] font-medium text-white backdrop-blur-md shadow-sm">
                <Maximize2 className="h-3 w-3" />
                <span>Enlarge</span>
              </span>
            </div>
          </div>
        </div>

        {/* 2. TITLE & PRACTITIONER CARD */}
        <div className="bg-white rounded-xl p-3 sm:p-4 border border-slate-200 shadow-2xs">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <h1 className="text-[15px] sm:text-[18px] font-bold text-slate-900 leading-snug">
                {classDetails.classTitle}
              </h1>
            </div>
            <button
              type="button"
              onClick={handleShare}
              className="flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-800 transition-all flex-shrink-0 cursor-pointer shadow-2xs"
              title="Share Session"
              aria-label="Share Session"
            >
              {isCopied ? (
                <Check className="w-3.5 h-3.5 text-emerald-600" />
              ) : (
                <Share2 className="w-3.5 h-3.5" />
              )}
            </button>
          </div>

          <div className="pt-2.5 text-[11px] sm:text-[12px] leading-tight font-normal space-y-1.5 border-t border-slate-100 mt-2.5">
            {/* Practitioner */}
            <div className="flex items-center gap-2">
              <UserCheck className="w-3.5 h-3.5 text-slate-700 flex-shrink-0" />
              <span className="text-slate-900 font-medium">
                {instructor?.name || (slug ? slug.replace(/-/g, ' ') : 'Wellness Mentor')}
                {instructor?.certification && (
                  <span className="text-slate-500 font-normal ml-1">
                    •{' '}
                    {Array.isArray(instructor.certification)
                      ? instructor.certification.join(', ')
                      : instructor.certification}
                  </span>
                )}
              </span>
            </div>

            {/* Mode & Platform */}
            <div className="flex items-center gap-2">
              {mode === 'online' ? (
                <Video className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
              ) : (
                <MapPin className="w-3.5 h-3.5 text-[#FF6B35] flex-shrink-0" />
              )}
              <span className="text-slate-700">
                {mode === 'online'
                  ? 'Online • Google Meet / Zoom'
                  : classDetails.venueAddress || 'Studio visit after booking'}
              </span>
            </div>

            {/* Duration & Timings */}
            <div className="flex items-center gap-2">
              <Clock className="w-3.5 h-3.5 text-slate-700 flex-shrink-0" />
              <span className="text-slate-700">
                {classDetails.classDuration ? `${classDetails.classDuration}` : '1 Hour'}
                {classDetails.schedule && Object.keys(classDetails.schedule).length > 0 && (
                  <span>
                    {' '}
                    •{' '}
                    {Object.keys(classDetails.schedule)
                      .map((day) => day.charAt(0).toUpperCase() + day.slice(1))
                      .join(', ')}
                  </span>
                )}
              </span>
            </div>
          </div>
        </div>

        {/* 3. HIGHLIGHTS & SESSION DETAILS */}
        <div className="bg-white rounded-xl p-3 sm:p-4 border border-slate-200 shadow-2xs">
          <h2 className="text-[12px] sm:text-[14px] font-semibold text-slate-900 tracking-tight mb-2">
            Session Details
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px] sm:text-[12px]">
            {classDetails.classStartDate && (
              <div className="flex items-start gap-2 text-slate-700">
                <span className="text-slate-900 text-xs leading-none mt-0.5">•</span>
                <div>
                  <span className="font-semibold text-slate-900">Dates: </span>
                  <span>
                    {classDetails.classEndDate
                      ? `${new Date(classDetails.classStartDate).toLocaleDateString('en-GB')} – ${new Date(classDetails.classEndDate).toLocaleDateString('en-GB')}`
                      : `Starts ${new Date(classDetails.classStartDate).toLocaleDateString('en-GB')} onwards`}
                  </span>
                </div>
              </div>
            )}
            {classDetails.classDuration && (
              <div className="flex items-start gap-2 text-slate-700">
                <span className="text-slate-900 text-xs leading-none mt-0.5">•</span>
                <div>
                  <span className="font-semibold text-slate-900">Duration: </span>
                  <span>
                    {classDetails.classDuration}{' '}
                    {!String(classDetails.classDuration)
                      .toLowerCase()
                      .includes(String(classDetails.durationUnit).toLowerCase()) &&
                    !String(classDetails.classDuration).toLowerCase().includes('min')
                      ? classDetails.durationUnit || ''
                      : ''}
                  </span>
                </div>
              </div>
            )}
            <div className="flex items-start gap-2 text-slate-700">
              <span className="text-slate-900 text-xs leading-none mt-0.5">•</span>
              <div>
                <span className="font-semibold text-slate-900">Session Type: </span>
                <span>
                  {classDetails?.classType?.toLowerCase() === 'fixed'
                    ? classDetails?.classEndDate
                      ? 'Fixed date class'
                      : 'One-to-one session'
                    : 'Scheduled batch session'}
                </span>
              </div>
            </div>
            {classDetails.schedule && Object.keys(classDetails.schedule).length > 0 && (
              <div className="flex items-start gap-2 text-slate-700">
                <span className="text-slate-900 text-xs leading-none mt-0.5">•</span>
                <div>
                  <span className="font-semibold text-slate-900">Schedule: </span>
                  <span>
                    {Object.keys(classDetails.schedule)
                      .map((day) => day.charAt(0).toUpperCase() + day.slice(1))
                      .join(' • ')}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 4. ABOUT THIS SESSION */}
        {(classDetails.classDescription || classDetails.description) && (
          <div className="bg-white rounded-xl p-3 sm:p-4 border border-slate-200 shadow-2xs">
            <h2 className="text-[12px] sm:text-[14px] font-semibold text-slate-900 tracking-tight mb-2">
              About This Session
            </h2>
            <div className="relative">
              <div
                className={`text-[12px] sm:text-[13px] leading-relaxed text-slate-700 space-y-2 ${
                  !isSummaryExpanded ? 'max-h-24 overflow-hidden' : ''
                }`}
              >
                <p className="whitespace-pre-line">
                  {classDetails.classDescription || classDetails.description}
                </p>
              </div>
              {!isSummaryExpanded && (
                <div className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-white via-white/90 to-transparent pointer-events-none" />
              )}
            </div>
            <div className="mt-2 flex justify-start">
              <button
                type="button"
                onClick={() => setIsSummaryExpanded(!isSummaryExpanded)}
                className="inline-flex items-center gap-1 text-[11.5px] font-bold text-slate-900 hover:underline cursor-pointer"
              >
                <span>{isSummaryExpanded ? 'See Less' : 'See More'}</span>
              </button>
            </div>
          </div>
        )}

        {/* 5. MODE SELECTION (If both modes available) */}
        {(!classDetails || classDetails.mode?.toLowerCase() === 'both') && (
          <div className="bg-white rounded-xl p-3 sm:p-4 border border-slate-200 shadow-2xs">
            <h2 className="text-[12px] sm:text-[14px] font-semibold text-slate-900 tracking-tight mb-2">
              Select Mode
            </h2>
            <div className="grid grid-cols-2 gap-2.5">
              <button
                type="button"
                onClick={() => setMode('online')}
                className={`p-3 rounded-xl border text-left transition-all cursor-pointer ${
                  mode === 'online'
                    ? 'border-emerald-600 bg-emerald-50/40 text-emerald-950 font-semibold shadow-xs'
                    : 'border-slate-200 bg-white hover:bg-slate-50 text-slate-700'
                }`}
              >
                <div className="text-[13px] font-semibold flex items-center gap-1.5">
                  <Video className="w-3.5 h-3.5 text-emerald-600" />
                  <span>Online</span>
                </div>
                <div className="text-[10.5px] text-slate-500 mt-0.5">Google Meet / Zoom</div>
              </button>
              <button
                type="button"
                onClick={() => setMode('offline')}
                className={`p-3 rounded-xl border text-left transition-all cursor-pointer ${
                  mode === 'offline'
                    ? 'border-emerald-600 bg-emerald-50/40 text-emerald-950 font-semibold shadow-xs'
                    : 'border-slate-200 bg-white hover:bg-slate-50 text-slate-700'
                }`}
              >
                <div className="text-[13px] font-semibold flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5 text-[#FF6B35]" />
                  <span>Offline</span>
                </div>
                <div className="text-[10.5px] text-slate-500 mt-0.5">Studio visit</div>
              </button>
            </div>
          </div>
        )}

        {/* OFFLINE VENUE DETAILS (Directly below Select Mode) */}
        {mode === 'offline' && (
          <div className="bg-white rounded-xl p-3 sm:p-4 border border-slate-200 shadow-2xs animate-fade-in">
            <h2 className="text-[12px] sm:text-[14px] font-semibold text-slate-900 tracking-tight mb-2">
              Venue Details
            </h2>
            <div className="flex gap-3 flex-wrap sm:flex-nowrap items-start">
              {classDetails?.venuePhoto && (
                <img
                  src={classDetails.venuePhoto}
                  alt="Venue"
                  className="w-24 h-20 rounded-lg object-cover border border-slate-200 shrink-0"
                />
              )}
              <div className="flex-1 text-[12px] text-slate-700">
                <p className="font-medium text-slate-900 mb-1">
                  {classDetails?.venueAddress || 'Studio visit at practitioner location (address details shared upon booking confirmation)'}
                </p>
                {classDetails?.venueLocation && (
                  <a
                    href={classDetails.venueLocation}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#FF6B35] font-semibold inline-flex items-center gap-1 hover:underline mt-1"
                  >
                    <MapPin className="w-3.5 h-3.5" />
                    <span>View on Google Maps</span>
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 6. SELECT PLAN / DURATION */}
        {activeDurations.length > 0 && (
          <div className="bg-white rounded-xl p-3 sm:p-4 border border-slate-200 shadow-2xs">
            <h2 className="text-[12px] sm:text-[14px] font-semibold text-slate-900 tracking-tight mb-2">
              Choose Plan
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              {activeDurations.map((d, index) => {
                const isSelected = selectedPlanIndex === index;
                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => setSelectedPlanIndex(index)}
                    className={`p-3 rounded-xl border text-left transition-all cursor-pointer ${
                      isSelected
                        ? 'border-black bg-slate-900 text-white shadow-xs'
                        : 'border-slate-200 bg-white hover:bg-slate-50 text-slate-900'
                    }`}
                  >
                    <div
                      className={`text-[10px] font-semibold uppercase tracking-wider mb-1 ${
                        isSelected ? 'text-slate-300' : 'text-slate-500'
                      }`}
                    >
                      {d.len}
                    </div>
                    <div
                      className={`text-[15px] sm:text-[16px] font-bold ${
                        isSelected ? 'text-white' : 'text-slate-900'
                      }`}
                    >
                      {d.amt}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* 8. FIXED BOTTOM BOOKING BAR (Matching luxury retreats) */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-slate-200/90 py-3 px-4 sm:px-8 shadow-[0_-8px_30px_rgba(0,0,0,0.08)]">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-3 sm:gap-4">
          {/* Price Info */}
          <div className="flex flex-col min-w-0">
            <span className="text-[9px] sm:text-[10px] font-semibold text-slate-500 uppercase tracking-wider leading-none mb-0.5">
              Selected Plan
            </span>
            <div className="flex items-baseline gap-1.5">
              <span className="text-base sm:text-[17px] font-bold text-slate-900 leading-tight">
                {currentPlan?.amt || '₹499'}
              </span>
              <span className="text-[10.5px] sm:text-[11px] text-slate-500 font-medium">
                / {currentPlan?.len?.toLowerCase() || 'session'}
              </span>
            </div>
          </div>

          {/* Action Button */}
          <div className="flex items-center flex-shrink-0">
            <button
              type="button"
              onClick={() => {
                const navState = { classDetails, instructor };
                navigate(
                  `/booking/${slug}/step2?classId=${classId}&plan=${currentPlan.len}&amt=${currentPlan.amt}&mode=${mode}&type=${serviceType}`,
                  { state: navState }
                );
              }}
              className="px-6 py-2.5 bg-black hover:bg-neutral-800 text-white rounded-full font-semibold text-[13px] sm:text-[14px] transition-all shadow-sm active:scale-95 cursor-pointer"
            >
              {currentPlan?.amt === '₹0'
                ? 'Continue to demo booking →'
                : isDateBasedPlan
                  ? 'Continue to date selection →'
                  : 'Continue to slot selection →'}
            </button>
          </div>
        </div>
      </div>

      {/* 9. IMAGE LIGHTBOX MODAL */}
      {isImageLightboxOpen && (
        <div
          onClick={() => setIsImageLightboxOpen(false)}
          className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/90 backdrop-blur-md p-4 cursor-pointer"
        >
          <button
            type="button"
            onClick={() => setIsImageLightboxOpen(false)}
            className="absolute top-5 right-5 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors cursor-pointer"
            aria-label="Close"
          >
            <X className="h-6 w-6" />
          </button>
          <img
            src={heroImage}
            alt={classDetails?.classTitle || 'Session Flyer'}
            className="max-h-[90vh] max-w-[90vw] object-contain rounded-xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default BookingStep1;
